import type {
  PokemonSelectPageData,
  OffencePokemonListData,
  DefencePokemonData,
  DefencePokemonListData,
  ComprehensiveOpponentListData,
  DamageSettingUiStateV1,
  StorageResult,
} from './types';
import { STORAGE_KEYS } from './types';
import { 
  getStorageItem, 
  setStorageItem, 
  removeStorageItem, 
  hasStorageItem,
  getStorageItemInfo
} from './localStorage';
import type { PokemonV2 } from '../types/pokemon';
import type { AttackPokemonData } from '../types/attack';

// 保存時刻を付与して再読み込み時の更新判断に使う
const withTimestamp = <T>(payload: T) => ({
  ...payload,
  timestamp: new Date().toISOString(),
});

// StorageResultのdataを取り出して型変換するユーティリティ
const mapDataResult = <T, R>(
  result: StorageResult<T | null>,
  mapper: (value: T) => R,
): StorageResult<R | null> => {
  if (!result.success) {
    return result;
  }
  if (!result.data) {
    return {
      success: true,
      data: null,
    };
  }
  return {
    success: true,
    data: mapper(result.data),
  };
};

/**
 * PokemonSelectPageデータの保存
 */
export function savePokemonSelectPageData(data: Omit<PokemonSelectPageData, 'timestamp'>): StorageResult<void> {
  return setStorageItem(STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA, withTimestamp(data));
}

/**
 * PokemonSelectPageデータの読み込み
 */
export function loadPokemonSelectPageData(): StorageResult<PokemonSelectPageData | null> {
  return getStorageItem<PokemonSelectPageData>(STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA);
}

/**
 * PokemonSelectPageデータの削除
 */
export function removePokemonSelectPageData(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA);
}

/**
 * PokemonSelectPageデータの存在確認
 */
export function hasPokemonSelectPageData(): boolean {
  return hasStorageItem(STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA);
}

/**
 * PokemonSelectPageデータの情報取得
 */
export function getPokemonSelectPageDataInfo() {
  return getStorageItemInfo(STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA);
}

/**
 * PokemonSelectPageデータの部分更新
 */
export function updatePokemonSelectPageData(partialData: Partial<Omit<PokemonSelectPageData, 'timestamp'>>): StorageResult<void> {
  const existingResult = loadPokemonSelectPageData();
  
  if (!existingResult.success) {
    return existingResult;
  }
  
  const existingData = (existingResult.data || {}) as PokemonSelectPageData;
  return setStorageItem(
    STORAGE_KEYS.POKEMON_SELECT_PAGE_DATA,
    withTimestamp({ ...existingData, ...partialData }),
  );
}

/**
 * OffencePokemonListの保存
 */
export function saveOffencePokemonList(offencePokemonList: PokemonV2[]): StorageResult<void> {
  return setStorageItem(
    STORAGE_KEYS.OFFENCE_POKEMON_LIST,
    withTimestamp({ offencePokemonList }),
  );
}

/**
 * OffencePokemonListの読み込み
 */
export function loadOffencePokemonList(): StorageResult<PokemonV2[] | null> {
  const result = getStorageItem<OffencePokemonListData>(STORAGE_KEYS.OFFENCE_POKEMON_LIST);
  return mapDataResult(result, (data) => data.offencePokemonList);
}

/**
 * OffencePokemonListの削除
 */
export function removeOffencePokemonList(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.OFFENCE_POKEMON_LIST);
}

/**
 * OffencePokemonListの存在確認
 */
export function hasOffencePokemonList(): boolean {
  return hasStorageItem(STORAGE_KEYS.OFFENCE_POKEMON_LIST);
}

/**
 * DefencePokemonの保存
 */
export function saveDefencePokemon(defencePokemon: PokemonV2): StorageResult<void> {
  return setStorageItem(
    STORAGE_KEYS.DEFENCE_POKEMON,
    withTimestamp({ defencePokemon }),
  );
}

/**
 * DefencePokemonの読み込み
 */
export function loadDefencePokemon(): StorageResult<PokemonV2 | null> {
  const result = getStorageItem<DefencePokemonData>(STORAGE_KEYS.DEFENCE_POKEMON);
  return mapDataResult(result, (data) => data.defencePokemon);
}

/**
 * DefencePokemonの削除
 */
export function removeDefencePokemon(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.DEFENCE_POKEMON);
}

/**
 * DefencePokemonの存在確認
 */
export function hasDefencePokemon(): boolean {
  return hasStorageItem(STORAGE_KEYS.DEFENCE_POKEMON);
}

/**
 * AttackPokemonListの保存（攻撃モード用）
 */
export function saveAttackPokemonList(attackPokemonList: AttackPokemonData[]): StorageResult<void> {
  return setStorageItem(
    STORAGE_KEYS.ATTACK_POKEMON_LIST,
    // 旧スキーマ互換のため defencePokemonList キーにまとめて保存する
    withTimestamp({ defencePokemonList: attackPokemonList }),
  );
}

/**
 * AttackPokemonListの読み込み（攻撃モード用）
 */
export function loadAttackPokemonList(): StorageResult<AttackPokemonData[] | null> {
  const result = getStorageItem<DefencePokemonListData>(STORAGE_KEYS.ATTACK_POKEMON_LIST);
  return mapDataResult(result, (data) => data.defencePokemonList);
}

/**
 * DefencePokemonListの保存
 */
export function saveDefencePokemonList(defencePokemonList: AttackPokemonData[]): StorageResult<void> {
  return setStorageItem(
    STORAGE_KEYS.DEFENCE_POKEMON_LIST,
    withTimestamp({ defencePokemonList }),
  );
}

/**
 * DefencePokemonListの読み込み
 */
export function loadDefencePokemonList(): StorageResult<AttackPokemonData[] | null> {
  const result = getStorageItem<DefencePokemonListData>(STORAGE_KEYS.DEFENCE_POKEMON_LIST);
  return mapDataResult(result, (data) => data.defencePokemonList);
}

/**
 * DefencePokemonListの削除
 */
export function removeDefencePokemonList(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.DEFENCE_POKEMON_LIST);
}

/**
 * DefencePokemonListの存在確認
 */
export function hasDefencePokemonList(): boolean {
  return hasStorageItem(STORAGE_KEYS.DEFENCE_POKEMON_LIST);
}

/**
 * 総合モードの相手リスト保存
 */
export function saveComprehensiveOpponentList(
  comprehensiveOpponentList: Omit<ComprehensiveOpponentListData, 'timestamp'>,
): StorageResult<void> {
  return setStorageItem(
    STORAGE_KEYS.COMPREHENSIVE_OPPONENT_LIST,
    withTimestamp(comprehensiveOpponentList),
  );
}

/**
 * 総合モードの相手リスト読み込み
 */
export function loadComprehensiveOpponentList(): StorageResult<ComprehensiveOpponentListData | null> {
  return getStorageItem<ComprehensiveOpponentListData>(STORAGE_KEYS.COMPREHENSIVE_OPPONENT_LIST);
}

/**
 * 総合モードの相手リスト削除
 */
export function removeComprehensiveOpponentList(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.COMPREHENSIVE_OPPONENT_LIST);
}

/**
 * 総合モードの相手リスト存在確認
 */
export function hasComprehensiveOpponentList(): boolean {
  return hasStorageItem(STORAGE_KEYS.COMPREHENSIVE_OPPONENT_LIST);
}

/**
 * 全ポケモンデータの削除
 */
export function removeAllPokemonData(): StorageResult<void> {
  const results = [
    removePokemonSelectPageData(),
    removeOffencePokemonList(),
    removeDefencePokemon(),
    removeDefencePokemonList(),
    removeComprehensiveOpponentList(),
  ];
  
  const failedResults = results.filter(result => !result.success);
  
  if (failedResults.length > 0) {
    return {
      success: false,
      error: `一部のデータ削除に失敗: ${failedResults.map(r => r.error).join(', ')}`
    };
  }
  
  return {
    success: true,
    data: undefined
  };
}

// -----------------------------
// ダメージ計算の追加設定UIの永続化
// -----------------------------

const createDefaultDamageSettingUiState = (): DamageSettingUiStateV1 => ({
  version: 1,
  timestamp: new Date().toISOString(),
  attackModeByKey: {},
  damageTaken: {
    selectedMoveKeys: [],
    moveSettingByKey: {},
  },
});

/**
 * ダメージ計算の追加設定UI状態を読み込む
 */
export function loadDamageSettingUiState(): StorageResult<DamageSettingUiStateV1 | null> {
  return getStorageItem<DamageSettingUiStateV1>(STORAGE_KEYS.DAMAGE_SETTING_UI_STATE);
}

/**
 * ダメージ計算の追加設定UI状態を更新する
 * - 読み込み→更新→保存をまとめて行う（上書き事故を避けるため必ず差分マージする）
 */
export function updateDamageSettingUiState(
  updater: (prev: DamageSettingUiStateV1) => DamageSettingUiStateV1,
): StorageResult<void> {
  const existingResult = loadDamageSettingUiState();
  if (!existingResult.success) {
    return existingResult;
  }

  const base = existingResult.data ?? createDefaultDamageSettingUiState();
  const next = updater(base);
  // timestamp は毎回更新して getItemInfo で追えるようにする
  return setStorageItem(
    STORAGE_KEYS.DAMAGE_SETTING_UI_STATE,
    { ...next, timestamp: new Date().toISOString(), version: 1 },
  );
}

/**
 * ダメージ計算の追加設定UI状態を削除する
 */
export function removeDamageSettingUiState(): StorageResult<void> {
  return removeStorageItem(STORAGE_KEYS.DAMAGE_SETTING_UI_STATE);
}
